import { useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Eye, Heart, SlidersHorizontal, Trash2, X } from "lucide-react";
import { PortalBreadcrumbs } from "./PortalBreadcrumbs";
import {
  operacionDisplay,
  operacionLabel,
  portalTitleFor,
  tipoPlural,
} from "@/lib/seo";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  AnunciosFiltersForm,
  countActiveFilters,
  emptyAnunciosFilters,
  serializeFilters,
  type AnunciosFilters,
} from "@/components/admin/AnunciosFiltersForm";
import {
  listAnunciosDemo,
  listAnunciosPublicos,
  listAnunciosVip,
} from "@/lib/anuncios.functions";
import {
  getDescartadosIds,
  getMyInteractions,
  listDescartadosIds,
  listFavoritosIds,
  toggleDescartado,
  toggleFavorito,
  trackImpresiones,
} from "@/lib/interacciones.functions";
import {
  decodePortalUrl,
  encodePortalUrl,
  filtersEqual,
  type PortalLookups,
} from "@/lib/portal-url";
import { usePortalFiltersSheet } from "@/contexts/portal-filters-sheet";
import { usePortalLookups } from "@/hooks/use-portal-lookups";
import { useSessionUserId, useVisitorId } from "@/hooks/use-portal-identity";
import { cn, formatPrice } from "@/lib/utils";
import { getDemandaMatches, markDemandaMatchesSeen } from "@/lib/demandas.functions";
import { getConectorLightDemandaMatches } from "@/lib/conector-light.functions";
import { listMisSolicitudesAnuncioIds } from "@/lib/solicitudes.functions";

type ListRow = {
  id: string;
  referencia: string;
  alias: string | null;
  direccion_completa: string | null;
  descripcion: string | null;
  estado: string;
  visibilidad: string;
  origen: string;
  precio_alquiler: number | null;
  precio_traspaso: number | null;
  precio_venta: number | null;
  rent_bruta: number | null;
  t_superficie: number | null;
  nivel_transito: string | null;
  t_esquinero: boolean | null;
  h_salida_humos: boolean | null;
  t_parking: boolean | null;
  t_parking_plazas: number | null;
  h_terraza: boolean | null;
  t_amueblado: boolean | null;
  ciudad_id: string | null;
  cp: string | null;
  created_at: string;
  cover_url: string | null;
  operacion: string | null;
  ciudad: string | null;
  provincia: string | null;
  barrio: string | null;
  tipo_local: string | null;
  usos_local: string[];
  agencia_id: string | null;
  agencia_nombre: string | null;
  agencia_logo_url: string | null;
  vistas_total: number | null;
  impresiones_total: number | null;
};

export type PortalSeoLabelsProp = {
  op?: string;
  tipoSlug?: string;
  tipoNombre?: string | null;
  provincia?: string | null;
  ciudad?: string | null;
  distrito?: string | null;
};

export function PortalPage({
  segments,
  mode = "all",
  basePath = "/portal",
  demandaId,
  demandaRef,
  seoLabels,
  light = false,
  vip = false,
  asignadoIds,
  seleccionIds,
  seleccionToken,
}: {
  segments: string[];
  mode?: "all" | "favoritos" | "descartados" | "vip" | "demo" | "demanda" | "miembro" | "seleccion";
  /** Modo selección: solo estos anuncios (los que un agente eligió para un cliente). */
  seleccionIds?: string[];
  /** Modo selección: se añade a los enlaces de las fichas para saber qué miró el cliente. */
  seleccionToken?: string;
  basePath?: string;
  /** Modo miembro: solo anuncios asignados a estos usuarios. */
  asignadoIds?: string[];
  demandaId?: string;
  demandaRef?: string | null;
  seoLabels?: PortalSeoLabelsProp;
  /** Flujo conector-light: la demanda es pública y el usuario puede ser anónimo. */
  light?: boolean;
  /** Fuerza el layout y los listados VIP (off-market) aunque el basePath no sea /portal-vip. */
  vip?: boolean;
}) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const isDemo = mode === "demo" || String(search.demo ?? "").toLowerCase() === "true";
  const userId = useSessionUserId();
  const visitorId = useVisitorId();
  const qc = useQueryClient();

  const listPub = useServerFn(listAnunciosPublicos);
  const listVip = useServerFn(listAnunciosVip);
  const listDemo = useServerFn(listAnunciosDemo);
  const isVipBase = basePath === "/portal-vip" || vip;

  const list = isDemo
    ? listDemo
    : mode === "vip" || isVipBase
      ? listVip
      : listPub;
  const fnDescartados = useServerFn(getDescartadosIds);
  const fnFavoritosIds = useServerFn(listFavoritosIds);
  const fnDescartadosIds = useServerFn(listDescartadosIds);
  const fnMyInter = useServerFn(getMyInteractions);
  const fnTrackImpr = useServerFn(trackImpresiones);
  const fnToggleFav = useServerFn(toggleFavorito);
  const fnToggleDesc = useServerFn(toggleDescartado);
  const fnDemandaMatchesAuth = useServerFn(getDemandaMatches);
  const fnDemandaMatchesLight = useServerFn(getConectorLightDemandaMatches);
  const fnDemandaMatches = light ? fnDemandaMatchesLight : fnDemandaMatchesAuth;
  const fnMarkSeen = useServerFn(markDemandaMatchesSeen);

  const fnMisSolicitudesIds = useServerFn(listMisSolicitudesAnuncioIds);

  const { lookups, lookupsReady } = usePortalLookups();

  const filters: AnunciosFilters = useMemo(
    () => decodePortalUrl(segments, search, lookups),
    [segments, search, lookups],
  );

  const active = countActiveFilters(filters);
  const { filtersOpen, setFiltersOpen } = usePortalFiltersSheet();
  const mobileFilterAnchorRef = useRef<HTMLDivElement | null>(null);
  const [mobileFilterStuck, setMobileFilterStuck] = useState(false);
  const [demandaTab, setDemandaTab] = useState<"todos" | "descartados" | "megusta">("todos");

  useEffect(() => {
    const HEADER_OFFSET = 74;
    const handleScroll = () => {
      const anchor = mobileFilterAnchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      setMobileFilterStuck(rect.top <= HEADER_OFFSET);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const PAGE_SIZE = 30;
  const pageParam = Number(search.page);
  const page = Number.isFinite(pageParam) && pageParam > 0 ? Math.floor(pageParam) : 1;

  // En la página pública de un agente no existen subrutas del portal:
  // navegar a `pathname` sacaría al visitante del perfil.
  const stayOnRoute = mode === "miembro" || mode === "seleccion";

  const setFilters = (next: AnunciosFilters) => {
    if (!lookupsReady) return;
    if (filtersEqual(next, filters)) return;
    const { pathname, search: nextSearch } = encodePortalUrl(next, lookups, basePath);
    const merged: Record<string, string> = { ...nextSearch };
    if (isDemo) merged.demo = "true";
    navigate({
      to: (stayOnRoute ? "." : pathname) as never,
      search: merged as never,
      replace: true,
    });
  };

  const setPage = (next: number) => {
    const { pathname, search: nextSearch } = encodePortalUrl(filters, lookups, basePath);
    const merged: Record<string, string> = { ...nextSearch };
    if (next > 1) merged.page = String(next);
    else delete merged.page;
    if (isDemo) merged.demo = "true";
    navigate({
      to: (stayOnRoute ? "." : pathname) as never,
      search: merged as never,
      replace: false,
    });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // IDs descartados por el usuario (logueado) → se ocultan del listing
  const descartadosQ = useQuery({
    queryKey: ["portal-descartados", userId ?? "anon"],
    queryFn: () => fnDescartados({ data: {} }) as Promise<{ ids: string[] }>,
    enabled: !!userId && mode !== "favoritos" && !isDemo,
  });

  // En modo favoritos limitamos por only_ids
  const favoritosQ = useQuery({
    queryKey: ["portal-favoritos-ids", userId ?? "anon"],
    queryFn: () => fnFavoritosIds() as Promise<{ ids: string[] }>,
    enabled: mode === "favoritos" && !!userId,
  });

  // En modo descartados limitamos por only_ids
  const descartadosIdsQ = useQuery({
    queryKey: ["portal-descartados-ids", userId ?? "anon"],
    queryFn: () => fnDescartadosIds() as Promise<{ ids: string[] }>,
    enabled: mode === "descartados" && !!userId,
  });

  // En modo demanda, obtenemos los IDs de anuncios que coinciden con la demanda.
  const demandaMatchesQ = useQuery({
    queryKey: ["portal-demanda-matches", demandaId ?? "none"],
    queryFn: () =>
      fnDemandaMatches({ data: { id: demandaId as string, limit: 5000 } }) as Promise<{
        items: Array<{ id: string; favorito?: boolean; descartado?: boolean; is_new_match?: boolean }>;
        total: number;
      }>,
    enabled: mode === "demanda" && !!demandaId,
  });

  const nuevosMatchSet = useMemo(
    () =>
      new Set(
        (demandaMatchesQ.data?.items ?? [])
          .filter((r) => r.is_new_match)
          .map((r) => r.id),
      ),
    [demandaMatchesQ.data?.items],
  );

  // Marca las coincidencias como vistas una vez cargadas (para limpiar el
  // badge "Nuevo" en la próxima visita).
  const seenMarkedRef = useRef<string | null>(null);
  useEffect(() => {
    if (mode !== "demanda" || !demandaId) return;
    if (light) return;
    if (!demandaMatchesQ.data) return;
    if (seenMarkedRef.current === demandaId) return;
    seenMarkedRef.current = demandaId;
    fnMarkSeen({ data: { id: demandaId } }).catch(() => {});
  }, [mode, demandaId, demandaMatchesQ.data, fnMarkSeen, light]);




  // IDs de anuncios en los que el usuario tiene solicitud enviada (para mostrar badge)
  const misSolicitudesQ = useQuery({
    queryKey: ["portal-mis-solicitudes-ids", userId ?? "anon"],
    queryFn: () => fnMisSolicitudesIds() as Promise<{ ids: string[] }>,
    enabled: !!userId,
    staleTime: 60_000,
  });
  const solicitadasSet = useMemo(
    () => new Set(misSolicitudesQ.data?.ids ?? []),
    [misSolicitudesQ.data?.ids],
  );

  const effective = isDemo
    ? { ...filters, estados: ["demo"], visibilidades: [] as string[] }
    : mode === "demanda"
      ? { ...filters, estados: ["publicado"], visibilidades: [] as string[] }
      : {
          ...filters,
          estados: ["publicado"],
          visibilidades: [mode === "vip" ? "off_market" : "publico"],
        };
  const excludeIds =
    mode === "seleccion" ||
    mode === "favoritos" ||
    mode === "descartados" ||
    (mode === "demanda" && demandaTab === "descartados")
      ? undefined
      : descartadosQ.data?.ids;
  const onlyIds = useMemo(() => {
    if (mode === "seleccion") return seleccionIds ?? [];
    if (mode === "favoritos") return favoritosQ.data?.ids ?? [];
    if (mode === "descartados") return descartadosIdsQ.data?.ids ?? [];
    if (mode === "demanda") {
      const items = demandaMatchesQ.data?.items ?? [];
      const descSet = new Set(descartadosQ.data?.ids ?? []);
      if (demandaTab === "descartados") {
        return items
          .filter((r) => r.descartado || descSet.has(r.id))
          .map((r) => r.id);
      }
      if (demandaTab === "megusta") {
        return items.filter((r) => r.favorito).map((r) => r.id);
      }
      return items
        .filter((r) => !r.descartado && !descSet.has(r.id))
        .map((r) => r.id);
    }
    return undefined;
  }, [
    mode,
    demandaTab,
    favoritosQ.data?.ids,
    descartadosIdsQ.data?.ids,
    demandaMatchesQ.data?.items,
    descartadosQ.data?.ids,
    seleccionIds,
  ]);

  // No bloqueamos el fetch principal por descartados/favoritos:
  // disparamos en paralelo y aplicamos exclude_ids cuando lleguen.
  const listEnabled =
    lookupsReady &&
    (mode === "favoritos"
      ? !!favoritosQ.data || !userId
      : mode === "descartados"
        ? !!descartadosIdsQ.data || !userId
        : mode === "demanda"
          ? !!demandaMatchesQ.data
          : true);


  const FAST_SIZE = 5;
  const offset = (page - 1) * PAGE_SIZE;

  const baseQueryArgs = {
    ...serializeFilters(effective),
    asignado_ids: mode === "miembro" && asignadoIds?.length ? asignadoIds : undefined,
    exclude_ids: excludeIds && excludeIds.length ? excludeIds : undefined,
    only_ids: mode !== "demanda" && onlyIds && onlyIds.length ? onlyIds : undefined,
  };

  const demandPageIds = mode === "demanda" ? (onlyIds ?? []) : undefined;
  const demandFastIds = demandPageIds?.slice(offset, offset + FAST_SIZE);
  const demandRestIds = demandPageIds?.slice(offset + FAST_SIZE, offset + PAGE_SIZE);
  const demandTotal = mode === "demanda" ? (demandPageIds?.length ?? 0) : undefined;

  const fastQ = useQuery({
    queryKey: [
      "portal-anuncios-fast",
      mode,
      asignadoIds ?? null,
      effective,
      excludeIds ?? null,
      onlyIds ?? null,
      offset,
      FAST_SIZE,
      demandFastIds ?? null,
    ],
    queryFn: () => {
      if ((mode === "favoritos" || mode === "descartados" || mode === "demanda" || mode === "seleccion") && (!onlyIds || onlyIds.length === 0)) {
        return Promise.resolve({ items: [] as ListRow[], total: demandTotal ?? 0 });
      }
      const data =
        mode === "demanda"
          ? {
              ...baseQueryArgs,
              only_ids: demandFastIds && demandFastIds.length ? demandFastIds : undefined,
              offset: 0,
              limit: FAST_SIZE,
              with_count: false,
            }
          : { ...baseQueryArgs, offset, limit: FAST_SIZE, with_count: true };
      return (list({ data }) as Promise<{ items: ListRow[]; total: number }>).then((result) =>
        mode === "demanda" ? { ...result, total: demandTotal ?? result.total } : result,
      );
    },
    enabled: listEnabled,
    placeholderData: keepPreviousData,
  });

  const total = mode === "demanda" ? (demandTotal ?? 0) : (fastQ.data?.total ?? 0);
  const hasRest = total > offset + FAST_SIZE;

  const restQ = useQuery({
    queryKey: [
      "portal-anuncios-rest",
      mode,
      asignadoIds ?? null,
      effective,
      excludeIds ?? null,
      onlyIds ?? null,
      offset + FAST_SIZE,
      PAGE_SIZE - FAST_SIZE,
      demandRestIds ?? null,
    ],
    queryFn: () => {
      if ((mode === "favoritos" || mode === "descartados" || mode === "demanda" || mode === "seleccion") && (!onlyIds || onlyIds.length === 0)) {
        return Promise.resolve({ items: [] as ListRow[], total: demandTotal ?? 0 });
      }
      const data =
        mode === "demanda"
          ? {
              ...baseQueryArgs,
              only_ids: demandRestIds && demandRestIds.length ? demandRestIds : undefined,
              offset: 0,
              limit: PAGE_SIZE - FAST_SIZE,
              with_count: false,
            }
          : {
              ...baseQueryArgs,
              offset: offset + FAST_SIZE,
              limit: PAGE_SIZE - FAST_SIZE,
              with_count: false,
            };
      return list({ data }) as Promise<{ items: ListRow[]; total: number }>;
    },
    enabled: listEnabled && fastQ.isSuccess && hasRest,
    placeholderData: keepPreviousData,
  });

  const pageData = useMemo(
    () => [
      ...(fastQ.data?.items ?? []),
      ...(restQ.data?.items ?? []),
    ],
    [fastQ.data, restQ.data],
  );
  const isLoading = !listEnabled || (fastQ.isFetching && !fastQ.data);
  const restLoading = hasRest && (!restQ.data || restQ.isFetching);
  const restPlaceholders = hasRest
    ? Math.min(PAGE_SIZE - FAST_SIZE, total - offset - FAST_SIZE)
    : 0;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const data = pageData;



  // Interacciones del usuario actual para los anuncios visibles en esta página
  const pageIds = pageData.map((r) => r.id);
  const interQ = useQuery({
    queryKey: ["portal-inter", userId ?? visitorId ?? "none", pageIds],
    queryFn: () =>
      fnMyInter({
        data: { anuncio_ids: pageIds, visitor_id: visitorId ?? undefined },
      }) as Promise<{
        items: Array<{ anuncio_id: string; favorito: boolean; descartado: boolean }>;
        userId: string | null;
      }>,
    enabled: pageIds.length > 0 && (!!userId || !!visitorId),
  });

  const interMap = useMemo(() => {
    const m = new Map<string, { favorito: boolean; descartado: boolean }>();
    for (const it of interQ.data?.items ?? []) {
      m.set(it.anuncio_id, { favorito: it.favorito, descartado: it.descartado });
    }
    return m;
  }, [interQ.data]);

  // Track impresiones cuando cambia la página de resultados
  useEffect(() => {
    if (pageIds.length === 0) return;
    if (!userId && !visitorId) return;
    const key = pageIds.join(",");
    const flag = `bz_impr_${userId ?? visitorId}_${key}`;
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(flag)) return;
    fnTrackImpr({
      data: { anuncio_ids: pageIds, visitor_id: visitorId ?? undefined },
    }).catch(() => {});
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(flag, "1");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIds.join(","), userId, visitorId]);

  const favMut = useMutation({
    mutationFn: (anuncioId: string) =>
      fnToggleFav({ data: { anuncio_id: anuncioId } }) as Promise<{ favorito: boolean }>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portal-inter"] });
      qc.invalidateQueries({ queryKey: ["portal-favoritos-ids"] });
      if (mode === "favoritos") qc.invalidateQueries({ queryKey: ["portal-anuncios"] });
      if (mode === "demanda") qc.invalidateQueries({ queryKey: ["portal-demanda-matches"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "No se pudo actualizar"),
  });

  const descMut = useMutation({
    mutationFn: (anuncioId: string) =>
      fnToggleDesc({ data: { anuncio_id: anuncioId } }) as Promise<{ descartado: boolean }>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portal-inter"] });
      qc.invalidateQueries({ queryKey: ["portal-descartados"] });
      qc.invalidateQueries({ queryKey: ["portal-descartados-ids"] });
      qc.invalidateQueries({ queryKey: ["portal-anuncios"] });
      if (mode === "demanda") qc.invalidateQueries({ queryKey: ["portal-demanda-matches"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "No se pudo actualizar"),
  });

  const requireLogin = () => {
    toast.message("Inicia sesión para guardar favoritos y descartar anuncios", {
      action: { label: "Entrar", onClick: () => navigate({ to: "/auth" as never }) },
    });
  };

  const isVipLayout = isVipBase || mode === "vip";

  return (
    <main
      className={
        isVipLayout
          ? "w-full px-4 sm:px-6 lg:px-10 xl:px-14 pt-[10px] pb-12"
          : "container mx-auto max-w-7xl px-4 sm:px-6 pt-[10px] pb-8"
      }
    >
      {mode === "all" && segments.length === 0 && active === 0 ? (
        <PortalSeoHeader segments={segments} seoLabels={seoLabels} />
      ) : mode === "miembro" || mode === "seleccion" ? null : (
        <h1 className="sr-only">Portal de locales comerciales, oficinas, naves y traspasos</h1>
      )}
      {mode !== "demanda" && mode !== "miembro" && mode !== "seleccion" && (
        <PortalBreadcrumbs mode={mode === "demo" ? "vip" : mode} filters={filters} lookups={lookups} basePath={basePath} />
      )}
      <div className={cn("grid gap-6", mode !== "demanda" && mode !== "miembro" && mode !== "seleccion" ? "lg:grid-cols-[330px_minmax(0,1fr)]" : mode === "miembro" || mode === "seleccion" ? "" : "lg:max-w-[calc(100%-354px)] lg:mx-auto")}>
        {mode !== "demanda" && mode !== "miembro" && mode !== "seleccion" && (
          <aside className="hidden lg:block space-y-3 lg:sticky lg:top-8 lg:self-start">
            <div className="rounded-lg border bg-card pt-4 pb-4 pl-4 pr-[5px]">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold">Filtros</h2>
                {active > 0 && (
                  <Badge
                    variant="secondary"
                    className="h-5 min-w-5 px-1.5 text-[10px]"
                  >
                    {active}
                  </Badge>
                )}
              </div>
              <div className="max-h-[calc(100vh-220px)] overflow-y-auto pr-1">
                <AnunciosFiltersForm
                  filters={filters}
                  onChange={setFilters}
                  hideInternal
                />
              </div>
              {active > 0 && (
                <button
                  type="button"
                  onClick={() => setFilters(emptyAnunciosFilters)}
                  className="w-full mt-3 text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          </aside>
        )}


        <section>
          <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-muted-foreground">
              {isLoading
                ? "Cargando…"
                : mode === "favoritos"
                  ? `${total} favoritos${totalPages > 1 ? ` · Página ${currentPage} de ${totalPages}` : ""}`
                  : mode === "descartados"
                    ? `${total} descartados${totalPages > 1 ? ` · Página ${currentPage} de ${totalPages}` : ""}`
                    : mode === "demanda"
                      ? `${total} coincidencias${totalPages > 1 ? ` · Página ${currentPage} de ${totalPages}` : ""}`
                      : mode === "miembro" || mode === "seleccion"
                        ? `${total} anuncios${totalPages > 1 ? ` · Página ${currentPage} de ${totalPages}` : ""}`
                      : `${total} resultados${totalPages > 1 ? ` · Página ${currentPage} de ${totalPages}` : ""}`}


            </div>
            <div className="flex gap-2">
            {mode !== "demanda" && mode !== "miembro" && mode !== "seleccion" && (
            <div className="lg:hidden" ref={mobileFilterAnchorRef}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={
                  mobileFilterStuck
                    ? "relative fixed top-[74px] left-4 z-30 shadow-md bg-background"
                    : "relative"
                }
                onClick={() => setFiltersOpen(true)}
              >
                <SlidersHorizontal className="h-4 w-4 mr-1" /> Filtros
                {active > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1 h-5 min-w-5 px-1.5 text-[10px]"
                  >
                    {active}
                  </Badge>
                )}
              </Button>
            </div>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="relative hidden"
              onClick={() => setFiltersOpen(true)}
            >
              <SlidersHorizontal className="h-4 w-4 mr-1" /> Filtros
              {active > 0 && (
                <Badge
                  variant="secondary"
                  className="ml-1 h-5 min-w-5 px-1.5 text-[10px]"
                >
                  {active}
                </Badge>
              )}
            </Button>
            {mode === "demanda" ? (
              <>
                <Button
                  type="button"
                  variant={demandaTab === "todos" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDemandaTab("todos")}
                >
                  Todos
                </Button>
                <Button
                  type="button"
                  variant={demandaTab === "descartados" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDemandaTab("descartados")}
                >
                  <Trash2 className="h-4 w-4 mr-0 sm:mr-1" />
                  <span className="hidden sm:inline">Descartados</span>
                </Button>
                <Button
                  type="button"
                  variant={demandaTab === "megusta" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDemandaTab("megusta")}
                >
                  <Heart className="h-4 w-4 mr-0 sm:mr-1" />
                  <span className="hidden sm:inline">Me gusta</span>
                </Button>
              </>
            ) : mode === "miembro" || mode === "seleccion" ? null : (
              <>
                <Button
                  asChild
                  variant={mode === "all" || mode === "vip" ? "default" : "outline"}
                  size="sm"
                >
                  <Link to={basePath as never}>Todos</Link>
                </Button>
                <Button
                  asChild
                  variant={mode === "descartados" ? "default" : "outline"}
                  size="sm"
                >
                  <Link to={`${basePath}/descartados` as never}>
                    <Trash2 className="h-4 w-4 mr-0 sm:mr-1" />
                    <span className="hidden sm:inline">Descartados</span>
                  </Link>
                </Button>
                <Button
                  asChild
                  variant={mode === "favoritos" ? "default" : "outline"}
                  size="sm"
                >
                  <Link to={`${basePath}/favoritos` as never}>
                    <Heart className="h-4 w-4 mr-1" /> Favoritos
                  </Link>
                </Button>
              </>
            )}
            </div>
          </div>
          {isLoading ? (
            <div className="flex flex-col gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-48 rounded-lg border bg-muted/30 animate-pulse"
                />
              ))}
            </div>
          ) : data.length === 0 ? (
            <div className="rounded-lg border bg-card p-10 text-center text-muted-foreground">
              {mode === "favoritos"
                ? userId
                  ? "Todavía no has añadido ningún anuncio a favoritos."
                  : "Inicia sesión para ver tus favoritos."
                : mode === "descartados"
                  ? userId
                    ? "Todavía no has descartado ningún anuncio."
                    : "Inicia sesión para ver tus descartados."
                  : mode === "demanda"
                    ? "Todavía no hay anuncios que coincidan con tu búsqueda."
                    : mode === "miembro"
                      ? "Este agente todavía no tiene anuncios publicados."
                    : mode === "seleccion"
                      ? "Estos locales ya no están disponibles."
                    : "No hay locales que coincidan con los filtros."}
            </div>
          ) : (
            <>
              <div
                className={
                  isVipLayout
                    ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-x-6 gap-y-10"
                    : "grid grid-cols-1 gap-4"
                }
              >
                {pageData.map((row, idx) => {
                  const st = interMap.get(row.id);
                  const cardProps = {
                    row,
                    favorito: st?.favorito ?? false,
                    descartado: st?.descartado ?? false,
                    solicitada: solicitadasSet.has(row.id),
                    nuevo: mode === "demanda" && nuevosMatchSet.has(row.id),
                    style: { animationDelay: `${idx * 60}ms` },
                    linkSearch: (mode === "demanda" && demandaId
                        ? { demanda: demandaId }
                        : mode === "seleccion" && seleccionToken
                          ? { sel: seleccionToken }
                          : undefined) as Record<string, string> | undefined,

                    onToggleFavorito: () => {
                      if (!userId) return requireLogin();
                      favMut.mutate(row.id);
                    },
                    onToggleDescartado: () => {
                      if (!userId) return requireLogin();
                      descMut.mutate(row.id);
                    },
                  };
                  return isVipLayout ? (
                    <VipAnuncioCard key={row.id} {...cardProps} />
                  ) : (
                    <AnuncioCard key={row.id} {...cardProps} />
                  );
                })}
                {restLoading &&
                  Array.from({ length: restPlaceholders }).map((_, i) => (
                    <div
                      key={`rest-skel-${i}`}
                      className={
                        isVipLayout
                          ? "aspect-[1/1.2] rounded-2xl bg-muted/30 animate-pulse"
                          : "h-48 rounded-lg border bg-muted/30 animate-pulse"
                      }
                    />
                  ))}
              </div>

              {totalPages > 1 && (
                <Pagination
                  page={currentPage}
                  totalPages={totalPages}
                  onChange={setPage}
                />
              )}
            </>
          )}
          {mode === "demanda" && demandaId && !isLoading && (
            <div className="mt-8 rounded-lg border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
              <p>
                Si no encontraste lo que buscas, recuerda que nuestra IA seguirá
                buscando tu local ideal.
              </p>
              <p className="mt-2">
                También puedes{" "}
                <Link
                  to={(light ? "/conector-light/$id" : "/conector/$id") as never}
                  params={{ id: demandaId } as never}
                  search={{ editar: "1" } as never}
                  className="font-medium text-primary underline underline-offset-4 hover:text-primary/80"
                >
                  editar tus requisitos aquí
                </Link>
                .
              </p>
              <p className="mt-4 text-xs">
                ID de demanda: <span className="font-mono font-medium text-foreground">{demandaRef ?? `D-${demandaId.replace(/-/g, "").slice(0, 6).toUpperCase()}`}</span>
              </p>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function AnuncioCard({
  row,
  favorito,
  descartado,
  solicitada,
  nuevo,
  style,
  linkSearch,
  onToggleFavorito,
  onToggleDescartado,
}: {
  row: ListRow;
  favorito: boolean;
  descartado: boolean;
  solicitada?: boolean;
  nuevo?: boolean;
  style?: React.CSSProperties;
  linkSearch?: Record<string, string>;
  onToggleFavorito: () => void;
  onToggleDescartado: () => void;
}) {

  const op = row.operacion?.toLowerCase() ?? "";

  return (
    <Card className="w-full h-full animate-slide-in-left" style={style}>
      <div className="flex flex-col sm:flex-row h-full sm:min-h-[230px] overflow-hidden rounded-xl shadow-sm hover:shadow transition-shadow">
        <Link
          to="/local/$id"
          params={{ id: row.id }}
          search={linkSearch as never}
          className="relative sm:w-72 sm:shrink-0 h-48 sm:h-full bg-muted overflow-hidden block"
          aria-label={`Ver detalles de ${row.usos_local[0] ?? row.tipo_local ?? "Local"}`}
        >
          {row.cover_url ? (
            <img
              src={row.cover_url}
              alt={`${row.usos_local[0] ?? row.tipo_local ?? "Local"}${row.operacion ? ` en ${row.operacion}` : ""}${row.ciudad ? ` en ${row.barrio ? `${row.barrio}, ${row.ciudad}` : row.ciudad}` : ""}`}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : null}
          <div className="absolute top-2 right-2 flex gap-1.5">
            <Button
              type="button"
              size="icon"
              variant="secondary"
              aria-label={favorito ? "Quitar de favoritos" : "Añadir a favoritos"}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleFavorito();
              }}
              className="h-8 w-8 bg-background/90 hover:bg-background backdrop-blur"
            >
              <Heart
                className={`h-4 w-4 ${favorito ? "fill-red-500 text-red-500" : "text-foreground"}`}
              />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="secondary"
              aria-label={descartado ? "Recuperar" : "Descartar"}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleDescartado();
              }}
              className="h-8 w-8 bg-background/90 hover:bg-background backdrop-blur"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {(solicitada || nuevo) && (
            <div className="absolute top-2 left-2 flex flex-col gap-1">
              {nuevo && (
                <div className="inline-flex items-center rounded-md bg-emerald-500/95 backdrop-blur px-2 py-0.5 text-[11px] font-semibold text-white shadow w-fit">
                  Nuevo
                </div>
              )}
              {solicitada && (
                <div className="inline-flex items-center rounded-md bg-primary/95 backdrop-blur px-2 py-0.5 text-[11px] font-semibold text-primary-foreground shadow w-fit">
                  Solicitud enviada
                </div>
              )}
            </div>
          )}

          {(row.vistas_total ?? 0) > 0 && (
            <div className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-md bg-background/85 backdrop-blur px-1.5 py-0.5 text-[11px] text-muted-foreground">
              <Eye className="h-3 w-3" />
              {row.vistas_total}
            </div>
          )}
        </Link>
        <CardContent className="flex-1 min-w-0 p-4 space-y-2 flex flex-col h-full">
          <div className="min-w-0 space-y-1">
            <h3 className="text-lg font-semibold break-words">
              <Link to="/local/$id" params={{ id: row.id }} search={linkSearch as never} className="hover:text-primary transition-colors">
                {row.usos_local[0] ?? row.tipo_local ?? "Local"}
                {row.operacion ? ` en ${row.operacion}` : ""}
                {row.barrio
                  ? ` en ${row.barrio}, ${row.ciudad}`
                  : row.ciudad
                    ? ` en ${row.ciudad}`
                    : ""}
              </Link>
            </h3>


            {op === "traspaso" && (
              <div className="flex flex-wrap gap-x-2 items-baseline text-lg font-bold shrink-0">
                {row.precio_traspaso != null && (
                  <span>
                    {formatPrice(row.precio_traspaso)} €{" "}
                    <span className="text-xs font-normal text-muted-foreground">
                      / Traspaso
                    </span>
                  </span>
                )}
                {row.precio_alquiler != null && (
                  <span>
                    {formatPrice(row.precio_alquiler)} €{" "}
                    <span className="text-xs font-normal text-muted-foreground">
                      / Alquiler
                    </span>
                  </span>
                )}
                {row.precio_traspaso == null && row.precio_alquiler == null && (
                  <span>Consultar</span>
                )}
              </div>
            )}
            {op === "venta" && (
              <div className="text-lg font-bold shrink-0">
                {row.precio_venta != null
                  ? `${formatPrice(row.precio_venta)} €`
                  : "Consultar"}
                {row.precio_venta != null && (
                  <span className="text-xs font-normal text-muted-foreground">
                    {" "}
                    / Venta
                  </span>
                )}
              </div>
            )}
            {op === "alquiler" && (
              <div className="text-lg font-bold shrink-0">
                {row.precio_alquiler != null
                  ? `${formatPrice(row.precio_alquiler)} €`
                  : "Consultar"}
                {row.precio_alquiler != null && (
                  <span className="text-xs font-normal text-muted-foreground">
                    {" "}
                    / Alquiler
                  </span>
                )}
              </div>
            )}
            {op === "rentabilidad" && (
              <div className="flex flex-wrap gap-x-2 items-baseline text-lg font-bold shrink-0">
                {row.precio_venta != null && (
                  <span>
                    {formatPrice(row.precio_venta)} €{" "}
                    <span className="text-xs font-normal text-muted-foreground">
                      / Venta
                    </span>
                  </span>
                )}
                {row.rent_bruta != null && (
                  <span className="text-sm font-normal text-muted-foreground">
                    {row.rent_bruta} % Rentabilidad
                  </span>
                )}
                {row.precio_venta == null && row.rent_bruta == null && (
                  <span>Consultar</span>
                )}
              </div>
            )}
            {! ["traspaso", "venta", "alquiler", "rentabilidad"].includes(op) && (
              <div className="flex flex-wrap gap-x-2 items-baseline text-lg font-bold shrink-0">
                {row.precio_alquiler != null && (
                  <span>
                    {formatPrice(row.precio_alquiler)} €{" "}
                    <span className="text-xs font-normal text-muted-foreground">
                      / Alquiler
                    </span>
                  </span>
                )}
                {row.precio_traspaso != null && (
                  <span>
                    {formatPrice(row.precio_traspaso)} €{" "}
                    <span className="text-xs font-normal text-muted-foreground">
                      / Traspaso
                    </span>
                  </span>
                )}
                {row.precio_venta != null && (
                  <span>
                    {formatPrice(row.precio_venta)} €{" "}
                    <span className="text-xs font-normal text-muted-foreground">
                      / Venta
                    </span>
                  </span>
                )}
                {row.precio_alquiler == null &&
                  row.precio_traspaso == null &&
                  row.precio_venta == null && <span>Consultar</span>}
              </div>
            )}

            {row.direccion_completa && (
              <p className="text-sm text-muted-foreground break-words">
                {row.direccion_completa}
              </p>
            )}
          </div>

          {row.descripcion && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {row.descripcion}
            </p>
          )}

          <Features row={row} op={op} />
        </CardContent>
      </div>
    </Card>
  );
}

function Features({ row, op }: { row: ListRow; op: string }) {
  const precioBase =
    op === "traspaso"
      ? (row.precio_traspaso ?? row.precio_alquiler ?? row.precio_venta)
      : op === "venta"
        ? (row.precio_venta ?? row.precio_alquiler ?? row.precio_traspaso)
        : (row.precio_alquiler ?? row.precio_venta ?? row.precio_traspaso);
  const precioM2 =
    row.t_superficie && row.t_superficie > 0 && precioBase != null
      ? precioBase / row.t_superficie
      : null;

  const items: string[] = [];
  if (row.t_superficie != null) items.push(`${row.t_superficie} m²`);
  if (precioM2 != null) items.push(`${formatPrice(precioM2)} €/m²`);
  if (row.nivel_transito)
    items.push(`Tráfico ${row.nivel_transito.toLowerCase().replace(/_/g, " ")}`);
  if (row.t_esquinero) items.push("Esquinero");
  if (row.h_salida_humos) items.push("Salida de humos");
  if (row.t_parking)
    items.push(
      row.t_parking_plazas ? `Parking (${row.t_parking_plazas})` : "Parking",
    );
  if (row.h_terraza) items.push("Terraza");
  if (row.t_amueblado) items.push("Amueblado");

  const hasAgencia =
    row.agencia_id && (row.agencia_logo_url || row.agencia_nombre);

  return (
    <div className="flex items-end justify-between gap-3 pt-1 sm:mt-auto">
      <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
        {items.map((label) => (
          <span
            key={label}
            className="inline-flex items-center rounded-md border bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground"
          >
            {label}
          </span>
        ))}
      </div>
      <div className="shrink-0 flex items-end justify-center h-20 w-28 overflow-hidden">
        {hasAgencia ? (
          row.agencia_logo_url ? (
            <img
              src={row.agencia_logo_url}
              alt={`Logo de ${row.agencia_nombre ?? "la agencia inmobiliaria"}`}
              loading="lazy"
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <span className="px-1 text-[10px] text-center text-muted-foreground line-clamp-2">
              {row.agencia_nombre}
            </span>
          )
        ) : null}
      </div>
    </div>
  );
}


function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  const pages: (number | "…")[] = [];
  const add = (p: number) => pages.push(p);
  const window = 1;
  const start = Math.max(2, page - window);
  const end = Math.min(totalPages - 1, page + window);
  add(1);
  if (start > 2) pages.push("…");
  for (let i = start; i <= end; i++) add(i);
  if (end < totalPages - 1) pages.push("…");
  if (totalPages > 1) add(totalPages);

  return (
    <div className="mt-6 flex items-center justify-center gap-1 flex-wrap">
      <Button
        variant="outline"
        size="sm"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        Anterior
      </Button>
      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`e${i}`} className="px-2 text-muted-foreground text-sm">
            …
          </span>
        ) : (
          <Button
            key={p}
            variant={p === page ? "default" : "outline"}
            size="sm"
            onClick={() => onChange(p)}
            className="min-w-9"
          >
            {p}
          </Button>
        ),
      )}
      <Button
        variant="outline"
        size="sm"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
      >
        Siguiente
      </Button>
    </div>
  );
}

function getMainPrice(row: ListRow): { amount: string; suffix: string } | null {
  const op = row.operacion?.toLowerCase() ?? "";
  const fmt = (n: number) => `${formatPrice(n)} €`;
  if (op === "alquiler" && row.precio_alquiler != null)
    return { amount: fmt(row.precio_alquiler), suffix: "/mes" };
  if (op === "traspaso" && row.precio_traspaso != null)
    return { amount: fmt(row.precio_traspaso), suffix: "" };
  if (op === "venta" && row.precio_venta != null)
    return { amount: fmt(row.precio_venta), suffix: "" };
  if (op === "rentabilidad" && row.precio_venta != null)
    return { amount: fmt(row.precio_venta), suffix: "" };
  if (row.precio_alquiler != null) return { amount: fmt(row.precio_alquiler), suffix: "/mes" };
  if (row.precio_traspaso != null) return { amount: fmt(row.precio_traspaso), suffix: "" };
  if (row.precio_venta != null) return { amount: fmt(row.precio_venta), suffix: "" };
  return null;
}

function VipAnuncioCard({
  row,
  favorito,
  solicitada,
  nuevo,
  style,
  onToggleFavorito,
}: {
  row: ListRow;
  favorito: boolean;
  descartado: boolean;
  solicitada?: boolean;
  nuevo?: boolean;
  style?: React.CSSProperties;
  linkSearch?: Record<string, string>;
  onToggleFavorito: () => void;
  onToggleDescartado: () => void;
}) {
  const op = row.operacion ?? "";
  const opLabel = op ? op.charAt(0).toUpperCase() + op.slice(1).toLowerCase() : "";
  const price = getMainPrice(row);
  const location = row.barrio
    ? `${row.barrio}, ${row.ciudad ?? ""}`
    : row.ciudad ?? row.provincia ?? "";
  const title = row.usos_local[0] ?? row.tipo_local ?? "Local comercial";
  const ariaTitle = `${title}${op ? ` en ${op}` : ""}${location ? ` en ${location}` : ""}`;
  const isNew = nuevo || (() => {
    if (!row.created_at) return false;
    const days = (Date.now() - new Date(row.created_at).getTime()) / 86400000;
    return days <= 14;
  })();


  return (
    <article
      className="group animate-slide-in-left"
      style={style}
    >
      <Link
        to="/local-vip/$id"
        params={{ id: row.id }}
        aria-label={`Ver detalles de ${ariaTitle}`}
        className="block"
      >
        <div className="relative overflow-hidden rounded-2xl aspect-[1/1.15] mb-4 bg-muted shadow-sm group-hover:shadow-xl transition-all duration-500">
          {row.cover_url ? (
            <img
              src={row.cover_url}
              alt={ariaTitle}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transform group-hover:scale-[1.06] transition-transform duration-[900ms] ease-out"
            />
          ) : null}

          {/* Top badges */}
          {(isNew || solicitada) && (
            <div className="absolute top-3 left-3 flex flex-col gap-1.5">
              {solicitada && (
                <span className="bg-primary/95 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] text-primary-foreground w-fit shadow">
                  Solicitud enviada
                </span>
              )}
              {isNew && (
                <span className="bg-foreground/85 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] text-background w-fit">
                  Nuevo
                </span>
              )}
            </div>
          )}

          {/* Heart */}
          <button
            type="button"
            aria-label={favorito ? "Quitar de favoritos" : "Añadir a favoritos"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleFavorito();
            }}
            className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-foreground/15 backdrop-blur-md border border-white/30 text-white hover:bg-background hover:text-red-500 transition-all"
          >
            <Heart className={`h-4 w-4 ${favorito ? "fill-red-500 text-red-500" : ""}`} />
          </button>

          {/* Operation tag */}
          {opLabel && (
            <div className="absolute bottom-3 left-3">
              <span className="bg-background px-3.5 py-1.5 rounded-xl text-[11px] font-bold uppercase tracking-wider text-foreground shadow-md">
                {opLabel}
              </span>
            </div>
          )}

          {/* Views */}
          {(row.vistas_total ?? 0) > 0 && (
            <div className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-background/85 backdrop-blur px-2 py-1 text-[10px] font-medium text-muted-foreground">
              <Eye className="h-3 w-3" />
              {row.vistas_total}
            </div>
          )}
        </div>

        <div className="px-0.5">
          <div className="flex justify-between items-start gap-3 mb-1.5">
            <h3 className="text-[15px] font-semibold text-foreground leading-snug line-clamp-2 group-hover:underline underline-offset-4 decoration-foreground/30">
              {title}
              {location ? ` · ${location}` : ""}
            </h3>
            {price ? (
              <span className="font-serif text-xl font-bold text-foreground tracking-tight whitespace-nowrap shrink-0">
                {price.amount}
                {price.suffix && (
                  <span className="font-sans text-[10px] font-normal text-muted-foreground ml-1">
                    {price.suffix}
                  </span>
                )}
              </span>
            ) : (
              <span className="font-serif text-base font-bold text-muted-foreground shrink-0">
                Consultar
              </span>
            )}
          </div>

          {location && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3 font-medium">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {location}
            </div>
          )}

          <div className="flex gap-6 border-t border-border/60 pt-3">
            {row.t_superficie != null && (
              <div className="flex flex-col">
                <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">
                  Superficie
                </span>
                <span className="text-sm font-bold text-foreground">
                  {row.t_superficie} m²
                </span>
              </div>
            )}
            {row.rent_bruta != null && (
              <div className="flex flex-col">
                <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">
                  Rentabilidad
                </span>
                <span className="text-sm font-bold text-foreground font-serif italic">
                  {row.rent_bruta}%
                </span>
              </div>
            )}
            {row.tipo_local && row.t_superficie == null && (
              <div className="flex flex-col">
                <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">
                  Tipo
                </span>
                <span className="text-sm font-bold text-foreground">
                  {row.tipo_local}
                </span>
              </div>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────
// SEO: visible H1 + intro paragraph rendered above the results.
// Renders resolved names when available (via seoLabels passed from the route
// loader); falls back to slug-humanization for the client-only mount that
// happens before hydration completes.
// ─────────────────────────────────────────────────────────────
function PortalSeoHeader({
  segments,
  seoLabels,
}: {
  segments: string[];
  seoLabels?: PortalSeoLabelsProp;
}) {
  const labels = {
    op: seoLabels?.op ?? segments[0] ?? "",
    tipoSlug: segments[1] ?? "",
    tipoNombre: seoLabels?.tipoNombre ?? null,
    provincia: seoLabels?.provincia ?? null,
    ciudad: seoLabels?.ciudad ?? null,
    distrito: seoLabels?.distrito ?? null,
  };
  const { h1 } = portalTitleFor(labels, segments.length);
  const opLower = operacionLabel(labels.op ?? "");
  const opDisplay = operacionDisplay(labels.op ?? "");
  const plural = tipoPlural(labels.tipoNombre ?? labels.tipoSlug);
  const loc =
    labels.distrito && labels.ciudad
      ? `${labels.distrito}, ${labels.ciudad}`
      : labels.ciudad || labels.provincia || "España";

  let intro = "";
  if (segments.length === 0) {
    intro =
      "Explora locales, oficinas y naves en traspaso, venta y alquiler en toda España. Filtra por ubicación, precio, superficie y tipo de negocio.";
  } else if (!opDisplay && labels.tipoNombre) {
    intro = `Descubre ${plural} disponibles en ${loc}. Compara precios, superficie y contacta directamente con el anunciante.`;
  } else if (opDisplay) {
    intro = `Encuentra ${plural} en ${opLower} en ${loc}. Anuncios verificados con detalles de precio, superficie y licencias, y asesoramiento humano de Bizaclia.`;
  } else {
    intro = `Descubre oportunidades inmobiliarias para negocios en ${loc} con acompañamiento humano de Bizaclia.`;
  }

  return (
    <header className="mb-2">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
        {h1}
      </h1>
      <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-3xl">
        {intro}
      </p>
    </header>
  );
}

